import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/Dashboard.css";
import Breadcrumb from "./Breadcrumb";
import { useMatter } from "../context/MatterContext";
import { useModules } from "../context/ModulesContext";
import NoOpenMatterPage from "./NoOpenMatterPage";
import { fetchEsignEnvelopeStatus, fetchSurveyStatus } from "../helpers/apiHelper";
import {
  buildEmbeddedQuestionnaireAlerts,
  buildEsignDocumentAlerts,
  findBhpRetainerModuleForMatter,
  getModuleContext,
  getModuleInstanceKey,
  isEsignDocumentModule,
  isRetainerSignedModule,
  mergeEsignStatusIntoModule,
  mergeSurveyStatusIntoModule,
  shouldShowProjectRFlowForMatter,
  selectAutoOpenEmbeddedQuestionnaire,
  selectAutoOpenEsignDocument,
} from "../modules/moduleUtils";
import PortalSurveyViewer from "./PortalSurveyViewer";
import PortalEsignViewer from "./PortalEsignViewer";
import Skeleton from "./Skeleton";
import BottomNav from "./BottomNav";
import { HiOutlineLightningBolt, HiOutlineNewspaper, HiChevronRight } from "react-icons/hi";
import { fetchNewsItems } from "../helpers/contentfulHelper";
import { PROMPT_FOR_MISSING_IMPORTANT_DOCUMENTS, fetchClientDocuments, missingMandatoryLabels } from "../helpers/documentsApi";
import ReactMarkdown from "react-markdown";

const RETAINER_PENDING_KEY = "bhp_retainer_pending";
const RETAINER_RETURN_PATH_KEY = "bhp_retainer_return_path";
const DEFAULT_RETAINER_RETURN_PATH = "/summary-updates";
const MODULE_POPUP_OPENED_PREFIX = "bhp_module_popup_opened::";
const INITIAL_NEWS_COUNT = 3;
const NEWS_BATCH_SIZE = 3;
const formatDate = (value) => {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};
const formatNewsSummary = (value) =>
  `${String(value || "").trim().replace(/[.!?…]+$/, "")}...`;
const getContentfulNewsSummary = (fields = {}) => {
  const description = fields.descriptionPt || fields.description || "";
  return description
    .replace(/!\[[^\]]*\]\([^)]+\)/g, "")
    .replace(/\[[^\]]+\]\([^)]+\)/g, "")
    .replace(/^[\s>#-]+/gm, "")
    .replace(/[*_~`]/g, "")
    .replace(/\s+/g, " ")
    .trim();
};
export const mapContentfulNewsItem = ({ sys = {}, fields = {} } = {}) => ({
  id: sys.id,
  title: fields.titlePt || fields.title || "Notícia",
  subtitle: getContentfulNewsSummary(fields),
  content: fields.descriptionPt || fields.description || "",
  publishedAt: fields.date || sys.createdAt,
});
const isNewsItem = ({ fields = {} } = {}) =>
  (fields.tags || []).some(
    (tag) => String(tag).trim().toLowerCase() === "news",
  );
const hasModulePopupBeenOpened = (moduleKey) => {
  if (!moduleKey) return false;
  try {
    return sessionStorage.getItem(`${MODULE_POPUP_OPENED_PREFIX}${moduleKey}`) === "true";
  } catch {
    return false;
  }
};
const markModulePopupOpened = (moduleKey) => {
  if (!moduleKey) return;
  try {
    sessionStorage.setItem(`${MODULE_POPUP_OPENED_PREFIX}${moduleKey}`, "true");
  } catch {}
};
export const buildMissingDocumentsAlert = (missingLabels) =>
  missingLabels && missingLabels.length
    ? {
        id: "missing_documents",
        title: "Documentos pendentes",
        message: `Ainda precisamos de: ${missingLabels.join(", ")}. Envie quando puder, em Documentos.`,
        cta: "Enviar documentos",
        action: "documents",
      }
    : null;

export const buildProfileAlerts = (party, matter, modules, showTerminationAlert) => {
  if (showTerminationAlert) {
    return [
      {
        id: "account_finalization_pending",
        title: "Caso em processo de encerramento",
        message:
          "Seu caso está em processo de encerramento. Se você não concorda com a decisão, poderá enviar uma disputa.",
        cta: "Ver detalhes",
        action: "dispute",
      },
    ];
  }

  const alerts = [];

  const email = party?.email || "";
  const phone = party?.phone || "";
  const pixKey = party?.bhprPixKey || "";

  const hasEmail = String(email).trim().length > 0;
  const hasPhone = String(phone).trim().length > 0;
  const hasPixKey = String(pixKey).trim().length > 0;

  const matterId = matter?.id || matter?.matterId || "";
  const retainerModule = findBhpRetainerModuleForMatter(modules, matterId);
  const projectRFlowPending = shouldShowProjectRFlowForMatter(modules, matterId);

  // Retainer alerts are only relevant when Project R is enabled for this matter.
  const retainerSigned = isRetainerSignedModule(retainerModule);

  const retainerPending = (() => {
    try {
      return localStorage.getItem(RETAINER_PENDING_KEY) === "true";
    } catch {
      return false;
    }
  })();

  const retainerReturnPath = (() => {
    try {
      return (
        localStorage.getItem(RETAINER_RETURN_PATH_KEY) ||
        DEFAULT_RETAINER_RETURN_PATH
      );
    } catch {
      return DEFAULT_RETAINER_RETURN_PATH;
    }
  })();

  if (projectRFlowPending && retainerPending && !retainerSigned) {
    alerts.push({
      id: "pending_new_retainer",
      title: "Contrato pendente",
      message: "Você precisa confirmar os novos termos para continuar.",
      cta: "Ver atualizações",
      action: retainerReturnPath,
    });
  }

  if (!hasPixKey) {
    alerts.push({
      id: "missing_pix",
      title: "Chave PIX ausente",
      message: "Adicione sua chave PIX no Perfil.",
      cta: "Adicionar no Perfil",
      action: "profile",
    });
  }

  if (!hasPhone) {
    alerts.push({
      id: "missing_phone",
      title: "Telefone ausente",
      message:
        "Inclua um telefone para que possamos falar com você quando necessário.",
      cta: "Adicionar no Perfil",
      action: "profile",
    });
  }

  if (!hasEmail) {
    alerts.push({
      id: "missing_email",
      title: "E-mail ausente",
      message:
        "Inclua seu e-mail para receber atualizações importantes do seu caso.",
      cta: "Adicionar no Perfil",
      action: "profile",
    });
  }

  if (projectRFlowPending && !retainerSigned && !retainerPending) {
    alerts.push({
      id: "missing_retainer",
      title: "Contrato pendente",
      message: "Seu contrato ainda não aparece como assinado.",
      cta: "Ver detalhes",
      action: "documents",
    });
  }

  return alerts;
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { party, matter, bootstrap, bootstrapping, bootstrapState } = useMatter();
  const { modules, setModules } = useModules();
  const [activeModule, setActiveModule] = useState(null);
  const [closingModule, setClosingModule] = useState(false);
  const [news, setNews] = useState([]);
  const [newsLoading, setNewsLoading] = useState(true);
  const [newsError, setNewsError] = useState(false);
  const [openNewsIds, setOpenNewsIds] = useState([]);
  const [visibleNewsCount, setVisibleNewsCount] = useState(INITIAL_NEWS_COUNT);
  const openedPopupKeysRef = useRef(new Set());
  const newsLoadMoreRef = useRef(null);
  useEffect(() => {
    let active = true;

    const loadNews = async () => {
      setNewsLoading(true);
      setNewsError(false);

      try {
        const items = await fetchNewsItems();
        if (!active) return;
        setNews(items.filter(isNewsItem).map(mapContentfulNewsItem));
        setOpenNewsIds([]);
        setVisibleNewsCount(INITIAL_NEWS_COUNT);
      } catch {
        if (!active) return;
        setNews([]);
        setNewsError(true);
      } finally {
        if (active) setNewsLoading(false);
      }
    };

    loadNews();

    return () => {
      active = false;
    };
  }, []);
  useEffect(() => {
    const run = async () => {
      if (bootstrapState === "no_open_matter") return;
      if (matter) return;
      // A bootstrap already in flight will re-run this effect when it settles, and a finished one
      // without a selected matter only needs the matter picker - fetching /me again would just
      // repeat the same request.
      if (bootstrapState === "loading") return;
      if (typeof bootstrap !== "function" || bootstrapState === "ready") {
        navigate("/", { replace: true });
        return;
      }
      const result = await bootstrap();
      if (!result?.ok) {
        if (result?.reason === "no_open_matter") {
          return;
        }
        navigate("/signin", { replace: true });
        return;
      }
      navigate("/", { replace: true });
    };
    run();
  }, [matter, bootstrap, bootstrapState, navigate]);

  useEffect(() => {
    if (activeModule || !Array.isArray(modules) || modules.length === 0) {
      return;
    }
    const isPopupAlreadyOpened = (moduleKey) =>
      openedPopupKeysRef.current.has(moduleKey) || hasModulePopupBeenOpened(moduleKey);
    const popupModule = [
      selectAutoOpenEmbeddedQuestionnaire(modules),
      selectAutoOpenEsignDocument(modules),
    ].find((candidate) => {
      const candidateKey = candidate && (getModuleInstanceKey(candidate) || candidate.id);
      return candidateKey && !isPopupAlreadyOpened(candidateKey);
    });
    if (!popupModule) {
      return;
    }
    const moduleKey = getModuleInstanceKey(popupModule) || popupModule.id;
    openedPopupKeysRef.current.add(moduleKey);
    markModulePopupOpened(moduleKey);
    setActiveModule(popupModule);
  }, [modules, activeModule]);
  useEffect(() => {
    const matterId = matter?.id || matter?.matterId || "";
    const projectRFlowPending = shouldShowProjectRFlowForMatter(modules, matterId);
    if (projectRFlowPending) return;
    try {
      localStorage.removeItem(RETAINER_PENDING_KEY);
      localStorage.removeItem(RETAINER_RETURN_PATH_KEY);
    } catch {}
  }, [modules, matter?.id, matter?.matterId]);
  const matterLabel = useMemo(() => matter?.name || "Meu Caso", [matter]);
  const hasCompensationScheme = useMemo(() => {
    const compensation = party?.compensation;
    return Boolean(compensation?.ff || compensation?.pid || compensation?.novel);
  }, [party]);
  const showTerminationAlert = useMemo(() => {
    const pendingTermination =
      String(matter?.matterStatus || "").trim() === "Pending Termination";
    return hasCompensationScheme && pendingTermination;
  }, [hasCompensationScheme, matter?.matterStatus]);
  const visibleNews = useMemo(
    () => news.slice(0, visibleNewsCount),
    [news, visibleNewsCount],
  );
  const hasMoreNews = visibleNewsCount < news.length;
  const fullName = useMemo(() => {
    const direct = party?.partyName || party?.name;
    if (direct) return String(direct).trim();
    const combined = [party?.firstName, party?.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();
    return combined || "Cliente";
  }, [party]);
  const [missingDocuments, setMissingDocuments] = useState([]);
  const partyIdForDocs = party?.id || party?.partyId || party?.Id || "";
  const matterIdForDocs = matter?.id || matter?.matterId || matter?.Id || "";
  useEffect(() => {
    const idToken = localStorage.getItem("idToken") || sessionStorage.getItem("idToken") || "";
    if (!PROMPT_FOR_MISSING_IMPORTANT_DOCUMENTS || !idToken || !partyIdForDocs || !matterIdForDocs) {
      setMissingDocuments([]);
      return undefined;
    }
    let cancelled = false;
    fetchClientDocuments(idToken, partyIdForDocs, matterIdForDocs)
      .then((resp) => {
        if (!cancelled) setMissingDocuments(missingMandatoryLabels(resp));
      })
      .catch((err) => {
        // Best effort: the dashboard must not break if the documents service is unavailable.
        console.error("Failed to check mandatory documents:", err);
        if (!cancelled) setMissingDocuments([]);
      });
    return () => {
      cancelled = true;
    };
  }, [partyIdForDocs, matterIdForDocs]);
  const profileAlerts = useMemo(() => {
    const baseAlerts = buildProfileAlerts(
      party,
      matter,
      modules,
      showTerminationAlert,
    );
    const moduleAlerts = [
      ...buildEsignDocumentAlerts(modules),
      ...buildEmbeddedQuestionnaireAlerts(modules),
    ];
    const documentsAlert = buildMissingDocumentsAlert(missingDocuments);
    return [...(documentsAlert ? [documentsAlert] : []), ...moduleAlerts, ...baseAlerts];
  }, [party, matter, modules, showTerminationAlert, missingDocuments]);
  useEffect(() => {
    if (!hasMoreNews || typeof IntersectionObserver === "undefined") {
      return undefined;
    }

    const target = newsLoadMoreRef.current;
    if (!target) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry?.isIntersecting) return;
        setVisibleNewsCount((current) =>
          Math.min(current + NEWS_BATCH_SIZE, news.length),
        );
      },
      {
        rootMargin: "160px 0px",
        threshold: 0.1,
      },
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [hasMoreNews, news.length]);
  const handleAlertAction = (action, alert) => {
    if (!action) return;
    if (action === "portal-module" || action === "portal-survey") {
      setActiveModule(alert?.module || alert?.notification || null);
      return;
    }
    if (String(action).startsWith("/")) {
      navigate(action);
      return;
    }
    if (action === "profile") {
      navigate("/profile");
      return;
    }
    if (action === "documents") {
      navigate("/documents");
      return;
    }
    if (action === "dispute") {
      navigate("/dispute");
    }
  };

  if (bootstrapState === "no_open_matter") {
    return <NoOpenMatterPage />;
  }
  const handleSurveyClose = async () => {
    if (closingModule) return;
    const closingQuestionnaireModule = activeModule;
    const context = getModuleContext(closingQuestionnaireModule);
    const invitationId = context?.invitationId;
    if (!invitationId) {
      setActiveModule(null);
      return;
    }
    try {
      setClosingModule(true);
      const token =
        localStorage.getItem("accessToken") ||
        sessionStorage.getItem("accessToken");
      if (!token) {
        setActiveModule(null);
        return;
      }
      const status = await fetchSurveyStatus(token, invitationId);
      if (!status) {
        setActiveModule(null);
        return;
      }
      setModules((currentModules) => {
        if (!Array.isArray(currentModules)) {
          return currentModules;
        }
        return currentModules.map((module) => {
          if (getModuleInstanceKey(module) !== invitationId) {
            return module;
          }
          return mergeSurveyStatusIntoModule(module, status);
        });
      });
      setActiveModule(null);
    } catch (error) {
      console.error("Failed to refresh survey status:", error);
      setActiveModule(null);
    } finally {
      setClosingModule(false);
    }
  };
  const handleEsignClose = async () => {
    if (closingModule) return;
    const closingEsignModule = activeModule;
    const envelopeId = getModuleContext(closingEsignModule)?.envelopeId;
    const token =
      localStorage.getItem("accessToken") || sessionStorage.getItem("accessToken");
    if (!envelopeId || !token) {
      setActiveModule(null);
      return;
    }
    try {
      setClosingModule(true);
      const status = await fetchEsignEnvelopeStatus(token, envelopeId);
      if (status) {
        setModules((currentModules) => {
          if (!Array.isArray(currentModules)) {
            return currentModules;
          }
          return currentModules.map((module) =>
            isEsignDocumentModule(module) && getModuleInstanceKey(module) === envelopeId
              ? mergeEsignStatusIntoModule(module, status)
              : module,
          );
        });
      }
    } finally {
      setActiveModule(null);
      setClosingModule(false);
    }
  };
  const handleToggleNews = (id) => {
    setOpenNewsIds((current) =>
      current.includes(id)
        ? current.filter((openId) => openId !== id)
        : [...current, id],
    );
  };
  const handleLoadMoreNews = () => {
    setVisibleNewsCount((current) =>
      Math.min(current + NEWS_BATCH_SIZE, news.length),
    );
  };
  if (!matter) {
    return (
      <div className="main-wrapper">
        <div className="dashPage">
          <div className="dashTop">
            <div className="dashHero">
              <div className="dashHello">Olá</div>

              {bootstrapping ? (
                <Skeleton variant="dashboard" />
              ) : (
                <div className="dashHeroLine">Nenhum caso selecionado.</div>
              )}

              <button
                type="button"
                className="dashPrimaryCta"
                onClick={() => navigate("/")}
                disabled={bootstrapping}
              >
                Escolher um caso
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-wrapper--dashboard">
      <Breadcrumb
        items={[{ label: "Meus Casos", link: "/multimatters" }, { label: matterLabel }]}
      />

      <div className="dashPage">
        <div className="dashBody">
          <div className="dashHello">Olá, {fullName}</div>

          {profileAlerts.length > 0 && (
            <section className="dashAlertSection" aria-label="Alertas do caso">
              <div className="dashAlertHeader">
                <span className="dashAlertBadge">Atenção</span>
                <div className="dashAlertTitle">
                  Itens que precisam da sua atenção
                </div>
              </div>

              <div className="dashAlertList">
                {profileAlerts.map((a) => (
                  <div key={a.id} className="dashAlertItem">
                    <div className="dashAlertItemText">
                      <div className="dashAlertItemTitle">{a.title}</div>
                      <div className="dashAlertItemMsg">{a.message}</div>
                    </div>

                    <button
                      type="button"
                      className="dashAlertCta"
                      onClick={() => handleAlertAction(a.action, a)}
                    >
                      {a.cta}
                    </button>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="surfaceDarkCard">
            <div className="sectionHeader sectionHeaderDark">
              <div className="sectionTitleDark"><HiOutlineLightningBolt style={{ color: "var(--color-warning, #fbbf24)" }} /> Ações rápidas</div>
            </div>

            <div className="dashActionsGrid">
              <button
                type="button"
                className="dashActionBtn"
                onClick={() => navigate("/profile")}
              >
                Meu perfil
              </button>

              <button
                type="button"
                className="dashActionBtn"
                onClick={() => navigate("/documents")}
              >
                Documentos
              </button>

              <button
                type="button"
                className="dashActionBtn"
                onClick={() => navigate("/faq")}
              >
                Perguntas frequentes
              </button>

              <button
                type="button"
                className="dashActionBtn dashActionBtnContact"
                onClick={() => navigate("/contact-us")}
              >
                Fale conosco
              </button>

            </div>
          </section>

          <section className="newsSectionDark">
            <div className="sectionHeader">
              <div className="sectionTitle"><HiOutlineNewspaper style={{ color: "var(--color-primary)" }} /> Notícias</div>
            </div>

            <div className="newsList">
              {newsLoading ? (
                <div className="emptyLight" role="status">
                  <div className="emptyLightTitle">Carregando notícias...</div>
                </div>
              ) : newsError ? (
                <div className="emptyLight" role="alert">
                  <div className="emptyLightTitle">
                    Não foi possível carregar as notícias
                  </div>
                  <div className="emptyLightSub">Tente novamente mais tarde.</div>
                </div>
              ) : visibleNews.length ? (
                visibleNews.map((n) => {
                  const isOpen = openNewsIds.includes(n.id);
                  const contentId = `news-content-${n.id}`;

                  return (
                    <article
                      key={n.id}
                      className={`newsCardBg${isOpen ? " is-open" : ""}`}
                    >
                      <button
                        type="button"
                        className="newsCardTrigger"
                        onClick={() => handleToggleNews(n.id)}
                        aria-expanded={isOpen}
                        aria-controls={contentId}
                      >
                        <div className="newsCardBgInner">
                          <div className="newsBgBody">
                            <div className="newsBgTitle">{n.title}</div>

                            <div className="newsBgDate">
                              {formatDate(n.publishedAt)}
                            </div>

                            {!isOpen && n.subtitle ? (
                              <div className="newsBgSub">
                                {formatNewsSummary(n.subtitle)}
                              </div>
                            ) : null}
                          </div>

                          <div className="newsBgChevron" aria-hidden="true">
                            <HiChevronRight />
                          </div>
                        </div>
                      </button>

                      {isOpen ? (
                        <div id={contentId} className="newsExpandedContent">
                          <ReactMarkdown
                            components={{
                              a: ({ children, ...props }) => (
                                <a
                                  {...props}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {children}
                                </a>
                              ),
                            }}
                          >
                            {n.content}
                          </ReactMarkdown>
                        </div>
                      ) : null}
                    </article>
                  );
                })
              ) : (
                <div className="emptyLight">
                  <div className="emptyLightTitle">Sem notícias no momento</div>
                  <div className="emptyLightSub">
                    Quando tivermos novidades, elas aparecerão aqui.
                  </div>
                </div>
              )}

              {hasMoreNews ? (
                <>
                  <div
                    ref={newsLoadMoreRef}
                    className="newsLoadMoreSentinel"
                    aria-hidden="true"
                  />
                  <button
                    type="button"
                    className="newsLoadMoreBtn"
                    onClick={handleLoadMoreNews}
                  >
                    Carregar mais notícias
                  </button>
                </>
              ) : null}
            </div>
          </section>

          <div className="bottomSpacer" />
        </div>
      </div>

      <BottomNav />

      {activeModule && isEsignDocumentModule(activeModule) && (
        <PortalEsignViewer
          module={activeModule}
          onClose={handleEsignClose}
          closing={closingModule}
        />
      )}
      {activeModule && !isEsignDocumentModule(activeModule) && (
        <PortalSurveyViewer
          module={activeModule}
          onClose={handleSurveyClose}
          closing={closingModule}
        />
      )}
    </div>
  );
}
